import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { ReactComponent as UnitedKingdomFlagIcon } from '../../assets/united-kingdom-flag.svg';
import { EventTitleEnum, LanguageCodeEnum } from '../../common/enums/common.enum';
import { PanelHeaderBox } from '../../pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration.styles';
import { useFormikContext } from 'formik';
import ToiletOptions from '../ToiletOptions/ToiletOptions';
import { FormValues } from '../../pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration';
import OptionOrChildEventFieldArray from './OptionOrChildEventFieldArray';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { getEventItemsForCount } from '../../utilities/helper/eventCount.helper';
import { useTranslation } from 'react-i18next';

import { palette } from '../../theme';
interface EventChildConfigurationProps {
  eventName: EventTitleEnum;
  eventIcons?: string[];
}

const EventChildConfiguration: React.FC<EventChildConfigurationProps> = ({ eventName, eventIcons }) => {
  const { t } = useTranslation();
  const { errors, touched, values, setFieldValue } = useFormikContext<FormValues>();
  const [secondaryLang, setSecondaryLang] = useState<LanguageCodeEnum>(LanguageCodeEnum.SRB);

  const childEvents = values.eventConfigs[eventName]?.childEvents ?? [];
  const options = values.eventConfigs[eventName]?.options ?? [];

  const allItems = getEventItemsForCount(eventName, childEvents, options);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box display="flex" gap="44px" alignItems="stretch">
        <Box flex={1} display="flex" flexDirection="column" border={`1px solid ${palette.line}`} borderRadius={2} sx={{ minHeight: '100%' }}>
          <PanelHeaderBox>
            <Box display="flex" alignItems="center" gap={1}>
              <Checkbox
                size="small"
                checked={values.eventConfigs[eventName]?.isDailyRequired ?? false}
                onChange={e => setFieldValue(`eventConfigs.${eventName}.isDailyRequired`, e.target.checked)}
              />
              <Typography variant="h5" fontWeight={400} color="text.primary">
                {t('events.mandatoryForDailyReport')}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <UnitedKingdomFlagIcon style={{ width: 27, height: 20 }} />
              <Typography variant="h5" fontWeight={400} color="text.primary">
                Eng
              </Typography>
            </Box>
          </PanelHeaderBox>
          <OptionOrChildEventFieldArray
            type="childEvents"
            eventName={eventName}
            lang={LanguageCodeEnum.ENG}
            eventIcons={eventIcons}
            fieldName={`eventConfigs.${eventName}.childEvents`}
            items={values.eventConfigs[eventName]?.childEvents ?? []}
          />
          {eventName === EventTitleEnum.TOILET ? (
            <ToiletOptions />
          ) : (
            <OptionOrChildEventFieldArray
              eventName={eventName}
              type="options"
              lang={LanguageCodeEnum.ENG}
              eventIcons={eventIcons}
              fieldName={`eventConfigs.${eventName}.options`}
              items={values.eventConfigs[eventName]?.options ?? []}
            />
          )}
        </Box>

        <Box flex={1} display="flex" flexDirection="column" border={`1px solid ${palette.line}`} borderRadius={2} sx={{ alignSelf: 'flex-start' }}>
          <PanelHeaderBox>
            <Box />
            <Box display="flex" alignItems="center" gap={1}>
              <LanguageSelector value={secondaryLang} onChange={setSecondaryLang} items={allItems} />
            </Box>
          </PanelHeaderBox>
          <OptionOrChildEventFieldArray
            type="childEvents"
            eventName={eventName}
            lang={secondaryLang}
            fieldName={`eventConfigs.${eventName}.childEvents`}
            items={values.eventConfigs[eventName]?.childEvents ?? []}
          />
          {eventName !== EventTitleEnum.TOILET && (
            <OptionOrChildEventFieldArray
              type="options"
              eventName={eventName}
              lang={secondaryLang}
              fieldName={`eventConfigs.${eventName}.options`}
              items={values.eventConfigs[eventName]?.options ?? []}
            />
          )}
        </Box>
      </Box>
      {errors.eventConfigs && touched.eventConfigs && <Typography color="error">{t('events.invalidConfigurations')}</Typography>}
    </Box>
  );
};

export default EventChildConfiguration;
